import { useQuery } from "@tanstack/react-query";
import { ActivitySentimentDetailDto, ContactSentimentEventDto } from "@/types";
import {
  fetchContactSentimentEvents,
  fetchActivitySentimentDetail,
} from "@/services/frontendServices/sentimentService";

export const sentimentKeys = {
  all: ["sentiment"] as const,
  events: () => [...sentimentKeys.all, "events"] as const,
  eventsByContact: (contactId: string) =>
    [...sentimentKeys.events(), contactId] as const,
  activityDetail: (activityId: string, contactId?: string) =>
    [
      ...sentimentKeys.all,
      "activity-detail",
      activityId,
      contactId ?? null,
    ] as const,
};

export const useContactSentimentEvents = (contactId: string) => {
  return useQuery<ContactSentimentEventDto[], Error>({
    queryKey: sentimentKeys.eventsByContact(contactId),
    queryFn: () => fetchContactSentimentEvents(contactId),
    enabled: !!contactId,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
};

/**
 * Loads the activity-detail read model for the SKR-725 modal. Fetches only when
 * `enabled` (e.g. the modal's `open` flag) AND an activityId is set — `enabled`
 * defaults to false so a caller that forgets the flag never fetches eagerly for
 * a hidden view.
 */
export const useActivitySentimentDetail = (
  activityId: string | null,
  contactId?: string,
  enabled = false
) => {
  return useQuery<ActivitySentimentDetailDto, Error>({
    queryKey: sentimentKeys.activityDetail(activityId ?? "", contactId),
    queryFn: () =>
      fetchActivitySentimentDetail(activityId as string, contactId),
    enabled: enabled && !!activityId,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
};
